import {
  ArrowUpIcon,
  CounterClockwiseClockIcon,
  DownloadIcon,
  LinkBreak1Icon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircledIcon,
  ResetIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Badge, Flex, FlexProps, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CustomIcon } from 'components/common/custom-icon';
import {
  AdminIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { EmptyBodyCell } from 'components/common/table/empty-body-cell';
import { useMachineSettingsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineSettingsDialogState';
import { MachinesTableDialogs } from 'components/sections/admin-portal/machines/machine-table-dialogs';
import { MachineUser } from 'components/sections/admin-portal/machines/machine-user';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachinesTableToolbar } from 'components/sections/admin-portal/machines/table-toolbar';
import { CheckedProvider } from 'contexts/layout/checked.context';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { lightFormat, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import {
  AdminPortalDialog,
  getMachineStatusColor,
  getMachineStatusDisplay,
  MachinesView,
} from 'enums/admin-portal';
import { CustomIconPath } from 'enums/custom.enums';
import { MachineSettingsSection } from 'enums/machine-settings';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IMenuAction } from 'interfaces/i-menus';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { getMachineActiveModelID, getModelKey } from 'lib_ts/classes/ms.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import {
  LOCAL_DATE_FORMAT,
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { QueueState } from 'lib_ts/enums/machine-msg.enum';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { isNil } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { AdminMachineModelsService } from 'services/admin/machine-models.service';
import { AdminMachinesService } from 'services/admin/machines.service';
import { TailscaleService } from 'services/tailscale.service';
import { TrainingLogsService } from 'services/training-logs.service';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';
import useMiscStore from 'stores/use-misc-store';
import './table.scss';

const PAGE_SIZES = TABLES.PAGE_SIZES.LG;
const ENABLE_LONG_PRESS_CONTEXT_MENU = true;

// group name starts with _ so that it renders as a separator in the menu
enum ActionGroup {
  Primary = '_1',
  Secondary = '_2',
  Test = '_Test',
}

const formatDate = (date?: string) =>
  date
    ? format(parseISO(date), LOCAL_DATETIME_FORMAT, {
        timeZone: LOCAL_TIMEZONE,
      })
    : '';

const StackedCellContainer = ({
  children,
  ...rest
}: PropsWithChildren<FlexProps>) => (
  <Flex
    direction="column"
    align="start"
    pt="3"
    pb="3"
    gap={RADIX.FLEX.GAP.XS}
    {...rest}
  >
    {children}
  </Flex>
);

export const MachinesTable = () => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const current = useAuthStore((s) => s.current);

  const loading = useMachinesPortalStore((s) => s.loading);
  const viewMode = useMachinesPortalStore((s) => s.viewMode);
  const machines = useMachinesPortalStore((s) => s.machines); // machine & model fetching handled in AdminPortal router
  const models = useMachinesPortalStore((s) => s.models);
  const filters = useMachinesPortalStore((s) => s.filters);
  const subscribed = useMachinesPortalStore((s) => s.subscribed);
  const tpSummaryDict = useMachinesPortalStore((s) => s.tpSummaryDict);
  const startSubscription = useMachinesPortalStore((s) => s.startSubscription);
  const stopSubscription = useMachinesPortalStore((s) => s.stopSubscription);
  const openDialog = useMachinesPortalStore((s) => s.openDialog);
  const setTpSummaryDict = useMachinesPortalStore((s) => s.setTpSummaryDict);

  const queueState = useMachineStatusStore((s) => s.queueState);
  const queueWsIDs = useMachineStatusStore((s) => s.queueWsIDs);

  const {
    isActive,
    machine: connectedMachine,
    openDialog: machineCxOpenDialog,
  } = useContext(MachineContext);

  const { openMachineSettingsDialog } = useMachineSettingsDialogState();

  const inQueue = queueWsIDs.includes(current.wsID);
  const userDisconnected = queueState === QueueState.UserDC;

  useEffect(() => {
    const cleanup = () => {
      stopSubscription();
    };

    // we are not yet connected to the server
    if (!inQueue || userDisconnected) {
      return cleanup;
    }

    startSubscription();
    return cleanup;
  }, [current.session, inQueue, userDisconnected]);

  // when view mode changes > fetch from the server
  useEffect(() => {
    if (viewMode !== MachinesView.TrainingPerformance) {
      return;
    }

    TrainingLogsService.getInstance()
      .summarize()
      .then((result) => {
        if (!result) {
          NotifyHelper.error({
            message_md: 'Failed to load training logs summary.',
          });
          return;
        }
        setTpSummaryDict(result);
      });
  }, [viewMode]);

  // TODO: Remove the custom className once menus / icon styling is fixed
  const columns = useMemo(() => {
    const output: ITypedTableColumn<IAdminMachine>[] = [
      {
        label: 'common.actions',
        key: ACTIONS_KEY,
        invisible: true,
        actions: [
          {
            group: ActionGroup.Primary,
            label: 'admin.export-sessions',
            prefixIcon: <DownloadIcon />,
            className: 'machine-portal-context-menu-item',
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.ExportSessions,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Primary,
            label: 'Restore Pitch Lists',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <CounterClockwiseClockIcon />,
            suffixIcon: <SuperAdminIcon />,
            invisibleFn: () => !userRoleFeatureFlags.adminMachines.restoreLists,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.RestoreList,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Primary,
            label: 'settings.reassign-pitch-lists',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <ListBulletIcon />,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.Reassign,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Secondary,
            label: 'admin.reset-connections',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <LinkBreak1Icon />,
            invisibleFn: (m) => !m.connected,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.ResetConnections,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Secondary,
            label: 'Run Health Check',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <MagnifyingGlassIcon />,
            suffixIcon: <SuperAdminIcon />,
            // must be active user to run health check
            invisibleFn: () =>
              !userRoleFeatureFlags.healthCheck ||
              !(connectedMachine.machineID && isActive),
            onClick: () => machineCxOpenDialog(MachineDialogMode.HealthCheck),
          },
          {
            group: ActionGroup.Secondary,
            label: 'Upgrade Firmware',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <ArrowUpIcon />,
            suffixIcon: <SuperAdminIcon />,
            invisibleFn: (m) =>
              !userRoleFeatureFlags.adminMachines.upgradeFirmware ||
              !m.connected,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.FWUpgradeDate,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Secondary,
            label: 'common.restart',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <ResetIcon />,
            color: RADIX.COLOR.WARNING,
            invisibleFn: (m) => !m.connected,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.RestartOS,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Secondary,
            label: 'Restart FW',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <CustomIcon icon={CustomIconPath.RestartMachine} />,
            suffixIcon: <SuperAdminIcon />,
            color: RADIX.COLOR.DANGER,
            invisibleFn: (m) =>
              !m.connected ||
              !userRoleFeatureFlags.adminMachines.restartFirmware,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.RestartFW,
                machines: [m],
              }),
          },
          {
            // should be mutually exclusive w/ resume install
            group: ActionGroup.Secondary,
            label: 'New Install',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <CustomIcon icon={CustomIconPath.NewInstall} />,
            suffixIcon: <SuperAdminIcon />,
            color: RADIX.COLOR.DANGER,
            invisibleFn: (m) => {
              if (!userRoleFeatureFlags.adminMachines.install) {
                return true;
              }

              if (m.machineID !== connectedMachine.machineID) {
                return true;
              }

              if (m.last_installation && !m.last_installation.end_date) {
                // show resume instead of new
                return true;
              }

              if (!connectedMachine.machineID) {
                return true;
              }

              if (!isActive) {
                return true;
              }

              return false;
            },
            onClick: () => machineCxOpenDialog(MachineDialogMode.Installation),
          },
          {
            // should be mutually exclusive w/ new install
            group: ActionGroup.Secondary,
            label: 'Resume Install',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <CustomIcon icon={CustomIconPath.NewInstall} />,
            suffixIcon: <SuperAdminIcon />,
            color: RADIX.COLOR.DANGER,
            invisibleFn: (m) => {
              if (!userRoleFeatureFlags.adminMachines.install) {
                return true;
              }

              if (m.machineID !== connectedMachine.machineID) {
                return true;
              }

              if (!m.last_installation || m.last_installation.end_date) {
                // show new instead of resume
                return true;
              }

              if (!connectedMachine.machineID) {
                return true;
              }

              if (!isActive) {
                return true;
              }

              return false;
            },
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.ConfirmInstall,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Secondary,
            label: 'Deactivate',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <TrashIcon />,
            suffixIcon: <SuperAdminIcon />,
            color: RADIX.COLOR.DANGER,
            invisibleFn: (m: IAdminMachine) =>
              m.deleted || !userRoleFeatureFlags.adminMachines.toggleDeleted,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.ToggleDeleted,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Secondary,
            label: 'Activate',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <CounterClockwiseClockIcon />,
            suffixIcon: <SuperAdminIcon />,
            invisibleFn: (m: IAdminMachine) =>
              !m.deleted || !userRoleFeatureFlags.adminMachines.toggleDeleted,
            onClick: (m) =>
              openDialog({
                type: AdminPortalDialog.ToggleDeleted,
                machines: [m],
              }),
          },
          {
            group: ActionGroup.Test,
            label: 'Auto-update Model',
            className: 'machine-portal-context-menu-item',
            prefixIcon: <QuestionMarkCircledIcon />,
            suffixIcon: <SuperAdminIcon />,
            color: RADIX.COLOR.DANGER,
            invisibleFn: () =>
              !userRoleFeatureFlags.adminMachines.autoUpdateModel,
            onClick: async (m) => {
              NotifyHelper.info({
                message_md: 'Processing request, please wait...',
              });

              const results =
                await AdminMachineModelsService.getInstance().testAutoUpdate(
                  m.machineID
                );

              if (!results) {
                NotifyHelper.error({
                  message_md: 'Auto-update model failed.',
                });
                return;
              }

              NotifyHelper.success({
                message_md: 'Auto-update model completed successfully!',
                buttons: [
                  {
                    label: 'Save Results',
                    dismissAfterClick: true,
                    onClick: () => {
                      MiscHelper.saveAs(
                        new Blob([JSON.stringify(results, null, 2)]),
                        `${m.machineID}-auto-update-model-result.json`
                      );
                    },
                  },
                ],
              });
            },
          },
        ],
      },
      {
        label: 'common.machine',
        key: 'machineID',
        tooltipFn: (m) => {
          if (m.super) {
            return 'Super machines can access every video';
          }
        },
        /** machineID (nickname, if applicable), * for super flag */
        formatFn: (m) => {
          const color = ((): RadixColor => {
            if (m.machineID === connectedMachine.machineID) {
              switch (queueState) {
                case QueueState.Active: {
                  return RADIX.COLOR.SUCCESS;
                }
                case QueueState.Busy: {
                  return RADIX.COLOR.WARNING;
                }

                case QueueState.UserDC:
                case QueueState.MachineDC:
                default: {
                  return RADIX.COLOR.DANGER;
                }
              }
            }

            return RADIX.COLOR.NEUTRAL;
          })();

          return (
            <StackedCellContainer>
              <Badge color={color}>
                {m.machineID} {m.super ? '*' : undefined}
              </Badge>
              {m.nickname && (
                <Text size={RADIX.TEXT.SIZE.XS} color={RADIX.COLOR.SECONDARY}>
                  {m.nickname}
                </Text>
              )}
            </StackedCellContainer>
          );
        },
      },
    ];

    switch (viewMode) {
      case MachinesView.DefaultView: {
        output.push(
          {
            label: 'admin.status',
            labelTooltip: t('admin.machine-status-label-tooltip').toString(),
            key: '_connection_status',
            sortRowsFn: (a, b, dir: number) => {
              return (a.connected ? 1 : 0) > (b.connected ? 1 : 0) ? -dir : dir;
            },
            formatFn: (m) => {
              const {
                connected,
                connect_date,
                disconnect_date,
                machine_status,
              } = m;

              const statusDate = connected ? connect_date : disconnect_date;
              const formattedStatusDate = statusDate
                ? formatDate(statusDate)
                : undefined;

              // we'll only have machine_status while we have an active summary subscription
              const showStatus = connected && subscribed && machine_status;
              return (
                <StackedCellContainer>
                  <Flex gap={RADIX.FLEX.GAP.XS} align="center">
                    <Badge
                      color={
                        connected ? RADIX.COLOR.SUCCESS : RADIX.COLOR.DANGER
                      }
                      mb="0"
                    >
                      {connected ? t('admin.online') : t('admin.offline')}
                    </Badge>
                    {showStatus && (
                      <Badge color={getMachineStatusColor(machine_status)}>
                        {getMachineStatusDisplay(machine_status)}
                      </Badge>
                    )}
                  </Flex>
                  {formattedStatusDate && (
                    <Text
                      size={RADIX.TEXT.SIZE.XS}
                      color={RADIX.COLOR.SECONDARY}
                    >
                      {formattedStatusDate}
                    </Text>
                  )}
                </StackedCellContainer>
              );
            },
          },
          {
            label: 'admin.last-fire',
            key: 'last_fire',
            formatFn: (m) => {
              if (!m.lastFire?._created) {
                return t('admin.none');
              }

              return (
                <StackedCellContainer>
                  <Text>{formatDate(m.lastFire._created)}</Text>
                  {m.lastFire.data.pitch_name && (
                    <Text
                      size={RADIX.TEXT.SIZE.XS}
                      color={RADIX.COLOR.SECONDARY}
                    >
                      {m.lastFire.data.pitch_name}
                    </Text>
                  )}
                </StackedCellContainer>
              );
            },
            sortRowsFn: (a, b, dir: number) =>
              -StringHelper.safeSort(
                a.lastFire?._created,
                b.lastFire?._created
              ) * dir,
          },
          {
            label: 'admin.active-ball-type',
            key: 'ball_type',
            labelTooltip: t('active-ball-type-label-tooltip').toString(),
            tooltipFn: (m) => {
              const activeKey = getModelKey(m);
              const activeID = getMachineActiveModelID(m);
              const activeModel = models.find((m) => m._id === activeID);

              if (activeModel) {
                return [
                  `Name: ${activeModel.name}`,
                  `Key: ${activeKey}`,
                  `ID: \`${activeID ?? 'none'}\``,
                ].join('\n\n');
              }

              return 'No active model';
            },
            formatFn: (m) => {
              const activeModelID = getMachineActiveModelID(m);
              const activeModel = models.find((m) => m._id === activeModelID);

              return (
                <StackedCellContainer>
                  <Text truncate>{m.ball_type}</Text>

                  {activeModel && (
                    <Flex width="100%">
                      <Text
                        size={RADIX.TEXT.SIZE.XS}
                        color={RADIX.COLOR.SECONDARY}
                        truncate
                      >
                        {activeModel.name}
                      </Text>
                    </Flex>
                  )}
                </StackedCellContainer>
              );
            },
          },
          {
            label: 'admin.firmware',
            key: 'last_firmware',
            formatFn: (m) => {
              if (!m.last_firmware) {
                return undefined;
              }

              return (
                <StackedCellContainer>
                  <Text
                    onClick={() => {
                      if (!m.last_firmware) {
                        return;
                      }

                      navigator.clipboard.writeText(m.last_firmware);

                      NotifyHelper.success({
                        message_md: t('common.x-copied-to-clipboard', {
                          x: m.last_firmware,
                        }),
                      });
                    }}
                    style={{
                      maxWidth: '100%',
                    }}
                    truncate
                  >
                    {m.last_firmware}
                  </Text>
                  {m.last_firmware_updated && (
                    <Text
                      size={RADIX.TEXT.SIZE.XS}
                      color={RADIX.COLOR.SECONDARY}
                    >
                      {formatDate(m.last_firmware_updated)}
                    </Text>
                  )}
                </StackedCellContainer>
              );
            },
          },
          {
            label: 'common.active-user',
            key: 'active',
            formatFn: (m) => {
              if (!m.active) {
                return t('admin.none');
              }

              return <MachineUser user={m.active} />;
            },
            sortRowsFn: (a, b, dir: number) =>
              -StringHelper.safeSort(a.active?.email, b.active?.email) * dir,
          }
        );
        break;
      }

      case MachinesView.TrainingPerformance: {
        output.push(
          {
            label: 'PT Success Rate',
            key: 'pt-success-rate',
            formatFn: (m) => {
              const entry = tpSummaryDict[m.machineID];

              if (isNil(entry)) {
                return <EmptyBodyCell />;
              }

              return (
                <Badge
                  color={
                    entry.ptSuccessRate >= 0.9
                      ? RADIX.COLOR.SUCCESS
                      : entry.ptSuccessRate >= 0.8
                        ? RADIX.COLOR.WARNING
                        : RADIX.COLOR.DANGER
                  }
                >
                  {(entry.ptSuccessRate * 100).toFixed(0)}%
                </Badge>
              );
            },
            sortRowsFn: (a, b, dir) => {
              const [aVal, bVal] = [a, b].map((m) => {
                const entry = tpSummaryDict[m.machineID];

                if (isNil(entry)) {
                  return -Infinity;
                }

                return entry.ptSuccessRate;
              });
              return aVal <= bVal ? -dir : dir;
            },
          },
          {
            label: 'Mean PT Rel. Thresholds',
            key: 'mean-pt-rel-thresholds',
            formatFn: (m) => {
              const entry = tpSummaryDict[m.machineID];

              if (isNil(entry)) {
                return <EmptyBodyCell />;
              }

              return entry.meanPtRelThresholds.toFixed(2);
            },
            sortRowsFn: (a, b, dir) => {
              const [aVal, bVal] = [a, b].map((m) => {
                const entry = tpSummaryDict[m.machineID];

                if (isNil(entry)) {
                  return -Infinity;
                }

                return entry.meanPtRelThresholds;
              });
              return aVal <= bVal ? -dir : dir;
            },
          },
          {
            label: 'Mean Excess Shots',
            key: 'mean-excess-shots',
            formatFn: (m) => {
              const entry = tpSummaryDict[m.machineID];

              if (isNil(entry)) {
                return <EmptyBodyCell />;
              }

              return entry.meanPtSuccessExcessShots.toFixed(2);
            },
            sortRowsFn: (a, b, dir) => {
              const [aVal, bVal] = [a, b].map((m) => {
                const entry = tpSummaryDict[m.machineID];

                if (isNil(entry)) {
                  return -Infinity;
                }

                return entry.meanPtSuccessExcessShots;
              });
              return aVal <= bVal ? -dir : dir;
            },
          },
          {
            label: 'Spin MAE (x, y, z)',
            key: 'spin-mae-x-y-z',
            formatFn: (m) => {
              const entry = tpSummaryDict[m.machineID];

              if (isNil(entry)) {
                return <EmptyBodyCell />;
              }

              return [entry.maeWXRpm, entry.maeWYRpm, entry.maeWZRpm]
                .map((n) => n.toFixed(0))
                .join(', ');
            },
            sortRowsFn: (a, b, dir) => {
              const [aVal, bVal] = [a, b].map((m) => {
                const entry = tpSummaryDict[m.machineID];

                if (isNil(entry)) {
                  return -Infinity;
                }

                return Math.max(entry.maeWXRpm, entry.maeWYRpm, entry.maeWZRpm);
              });
              return aVal <= bVal ? -dir : dir;
            },
          },
          {
            label: 'Break MAE (x, z)',
            key: 'break-mae-x-z',
            formatFn: (m) => {
              const entry = tpSummaryDict[m.machineID];

              if (isNil(entry)) {
                return <EmptyBodyCell />;
              }

              return [entry.maeBreakXIn, entry.maeBreakZIn]
                .map((n) => n.toFixed(1))
                .join(', ');
            },
            sortRowsFn: (a, b, dir) => {
              const [aVal, bVal] = [a, b].map((m) => {
                const entry = tpSummaryDict[m.machineID];

                if (isNil(entry)) {
                  return -Infinity;
                }

                return Math.max(entry.maeBreakXIn, entry.maeBreakZIn);
              });
              return aVal <= bVal ? -dir : dir;
            },
          },
          {
            label: 'Plate MAE (x, z)',
            key: 'plate-mae-x-z',
            formatFn: (m) => {
              const entry = tpSummaryDict[m.machineID];

              if (isNil(entry)) {
                return <EmptyBodyCell />;
              }

              return [entry.maePlateXIn, entry.maePlateZIn]
                .map((n) => n.toFixed(1))
                .join(', ');
            },
            sortRowsFn: (a, b, dir) => {
              const [aVal, bVal] = [a, b].map((m) => {
                const entry = tpSummaryDict[m.machineID];

                if (isNil(entry)) {
                  return -Infinity;
                }

                return Math.max(entry.maePlateXIn, entry.maePlateZIn);
              });
              return aVal <= bVal ? -dir : dir;
            },
          }
        );
        break;
      }
    }

    return output;
  }, [
    viewMode,
    userRoleFeatureFlags,
    models,
    connectedMachine.machineID,
    queueState,
    isActive,
    subscribed,
    tpSummaryDict,
  ]);

  const checkedActions = useCallback(
    (checked: IAdminMachine[]) => {
      const connected = checked.filter((m) => m.connected);

      const output: IMenuAction[] = [
        {
          label: 'Download Config',
          invisible: connected.length === 0,
          onClick: async () => {
            for (const machineID of connected.map((c) => c.machineID)) {
              const result =
                await AdminMachinesService.getInstance().getConfig(machineID);

              if (!result?.config) {
                NotifyHelper.warning({
                  message_md: `Empty value for ${machineID}, nothing to download.`,
                });
                return;
              }

              MiscHelper.saveAs(
                new Blob([JSON.stringify(result.config, null, 2)]),
                `${machineID}.json`
              );
            }
          },
        },
        {
          label: 'admin.export-sessions',
          suffixIcon: <AdminIcon />,
          onClick: () =>
            openDialog({
              type: AdminPortalDialog.ExportSessions,
              machines: checked,
            }),
        },
        {
          label: 'common.restart',
          color: RADIX.COLOR.WARNING,
          invisible: connected.length === 0,
          onClick: () =>
            openDialog({
              type: AdminPortalDialog.RestartOS,
              machines: connected,
            }),
        },
        {
          label: 'Restart FW',
          suffixIcon: <SuperAdminIcon />,
          color: RADIX.COLOR.DANGER,
          invisible:
            !userRoleFeatureFlags.adminMachines.restartFirmware ||
            connected.length === 0,
          onClick: () =>
            openDialog({
              type: AdminPortalDialog.RestartFW,
              machines: connected,
            }),
        },
        {
          label: 'Upgrade Firmware',
          suffixIcon: <SuperAdminIcon />,
          color: RADIX.COLOR.WARNING,
          invisible:
            !userRoleFeatureFlags.adminMachines.upgradeFirmware ||
            connected.length === 0,
          onClick: () =>
            openDialog({
              type: AdminPortalDialog.FWUpgradeDate,
              machines: connected,
            }),
        },
        {
          label: 'Create Tailscale Key',
          suffixIcon: <SuperAdminIcon />,
          color: RADIX.COLOR.WARNING,
          invisible:
            !userRoleFeatureFlags.adminMachines.createTailscaleKey ||
            connected.length === 0,
          onClick: async () => {
            for (const machine of connected) {
              const success =
                await TailscaleService.getInstance().createAuthKey(
                  machine.machineID
                );

              if (success) {
                NotifyHelper.success({
                  message_md: `Created key for ${machine.machineID}!`,
                });
              }

              // add a slight delay between each request in case there are a ton of machines
              await MiscHelper.sleep(500);
            }
          },
        },
      ];

      return output;
    },
    [current.role, openDialog]
  );

  const filtered = useMemo(() => {
    return machines
      .filter(
        (m) =>
          filters.isConnected === undefined ||
          m.connected === filters.isConnected
      )
      .filter(
        (m) =>
          filters.statuses.length === 0 ||
          (m.machine_status && filters.statuses.includes(m.machine_status))
      )
      .filter(
        (m) =>
          filters.hasActive === undefined || !m.active === !filters.hasActive
      )
      .filter(
        (m) =>
          filters.machineIDs.length === 0 ||
          filters.machineIDs.includes(m.machineID)
      )
      .filter(
        (m) =>
          filters.teams.length === 0 || filters.teams.includes(m._parent_id)
      )
      .filter(
        (m) =>
          filters.created.length === 0 ||
          (m._created &&
            filters.created.includes(
              lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT)
            ))
      )
      .filter(
        (m) =>
          filters.firmware.length === 0 ||
          (m.last_firmware &&
            filters.firmware.includes(m.last_firmware.split(' ')[0]))
      )
      .filter(
        // since deleted can be undefined or a boolean
        (m) =>
          filters.isDeleted === undefined || filters.isDeleted === !!m.deleted
      );
  }, [machines, filters]);

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminMachineList,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'machineID',
      dir: -1,
    },
  };

  const select = useMemo(() => {
    const output: ITableSelectable = {
      enableSelect: true,
      afterChangeSelected: (m) =>
        openMachineSettingsDialog(
          m,
          viewMode === MachinesView.DefaultView
            ? undefined // default view opens to the first section
            : MachineSettingsSection.TrainingPerformance // if viewing training performance, cut to that section
        ),
    };

    return output;
  }, [viewMode]);

  return (
    <ErrorBoundary identifier="MachinesTable">
      <CheckedProvider data={filtered}>
        <FlexTableWrapper
          gap={RADIX.FLEX.GAP.SECTION}
          table={
            <CommonTableHoC
              dataTestID="AdminMachines"
              identifier="AdminMachinesTable"
              data-testid="AdminMachines"
              toolbarContent={<MachinesTableToolbar />}
              displayColumns={columns}
              displayData={filtered}
              enableListener={!anyOpenDialogs}
              checkboxColumnIndex={0}
              checkedActions={checkedActions}
              loading={loading}
              enableLongPressContextMenu={ENABLE_LONG_PRESS_CONTEXT_MENU}
              {...pagination}
              {...select}
              {...sort}
              skipResetSelected
              vFlex
            />
          }
        />
      </CheckedProvider>
      <MachinesTableDialogs />
    </ErrorBoundary>
  );
};
